import React, { useState, useContext, useEffect, useRef } from 'react';
import { X, Send, Calculator, Repeat } from 'lucide-react';
import { translations, calculateEgyptTax } from '../data/translations';
import { useLanguage } from '../contexts/LanguageContext';

export default function FloatingChatBot({ isOpen: externalIsOpen, onClose, onOpen }) {
  const { language } = useLanguage();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [showPromptMessage, setShowPromptMessage] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: language === 'ar'
        ? 'أنا مساعد M.A.S الذكي. يمكنني حساب ضريبة الدخل السنوية لك. من فضلك اختر هل أنت شركة أم فرد.'
        : "I'm M.A.S Smart Assistant. I can calculate your annual income tax. Please choose whether you are a company or an individual."
    }
  ]);
  const [input, setInput] = useState('');
  const [conversationStep, setConversationStep] = useState('ask_user_type');
  const [userIncome, setUserIncome] = useState(0);
  const [userType, setUserType] = useState(''); // 'company' or 'individual'
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesContainerRef = useRef(null);

  // Format number with commas for thousands - ALWAYS use English numerals
  const formatNumberWithCommas = (number) => {
    if (!number && number !== 0) return '';
    
    // Remove any non-digit characters except decimal point and minus
    const numStr = number.toString().replace(/[^\d.-]/g, '');
    const num = parseFloat(numStr);
    
    if (isNaN(num)) return '';
    
    // ALWAYS use English numerals (1,2,3) regardless of language
    // This ensures numbers are always in English format
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: true
    });
  };

  // Handle input change - allow both numbers and text
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Check if we're in a number-only step
    const isNumberStep = conversationStep === 'initial';
    
    if (isNumberStep) {
      // For number steps: allow ONLY English digits (0-9) and decimal point
      // Remove Arabic numerals, letters, and other characters
      // Allow: 0-9 and .
      const cleanValue = value.replace(/[^0-9.]/g, '');
      
      // Prevent multiple decimal points
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        setInput(parts[0] + '.' + parts.slice(1).join(''));
        return;
      }
      
      // Format with commas
      if (cleanValue && cleanValue !== '.') {
        const num = parseFloat(cleanValue);
        if (!isNaN(num)) {
          setInput(formatNumberWithCommas(num));
        } else {
          setInput(cleanValue);
        }
      } else {
        setInput(cleanValue);
      }
    } else {
      // For text steps: allow all characters (for yes/no, company/individual)
      setInput(value);
    }
  };

  // Use external control if provided, otherwise use internal state
  const isExternallyControlled = externalIsOpen !== undefined;
  const isOpen = isExternallyControlled ? externalIsOpen : internalIsOpen;

  const openChat = () => {
    if (isExternallyControlled) {
      if (onOpen) onOpen();
      return;
    }
    setInternalIsOpen(true);
  };

  const closeChat = () => {
    if (isExternallyControlled) {
      if (onClose) onClose();
      return;
    }
    setInternalIsOpen(false);
  };

  // Reset conversation when language changes
  useEffect(() => {
    resetConversation();
  }, [language]);

  const resetConversation = () => {
    setMessages([{
      role: 'bot',
      text: language === 'ar'
        ? 'أنا مساعد M.A.S الذكي. يمكنني حساب ضريبة الدخل السنوية لك. من فضلك اختر هل أنت شركة أم فرد.'
        : "I'm M.A.S Smart Assistant. I can calculate your annual income tax. Please choose whether you are a company or an individual."
    }]);
    setConversationStep('ask_user_type');
    setUserIncome(0);
    setUserType('');
    setInput('');
  };

  const calculateTaxForHighIncome = (income, type) => {
    let tax = 0;
    
    if (type === 'company') {
      // Company: income * 22.5%
      tax = income * 0.225;
      return tax;
    }
    
    // Individual calculations
    if (income <= 600000) {
      if (income <= 40000) tax = 0;
      else if (income <= 55000) tax = (income - 40000) * 0.10;
      else if (income <= 70000) tax = 1500 + (income - 55000) * 0.15;
      else if (income <= 200000) tax = 3750 + (income - 70000) * 0.225;
      else if (income <= 400000) tax = 31000 + (income - 200000) * 0.225;
      else tax = 76000 + (income - 400000) * 0.25;
    } 
    else if (income <= 700000) {
      if (income <= 55000) tax = income * 0.10;
      else if (income <= 70000) tax = 5500 + (income - 55000) * 0.15;
      else if (income <= 200000) tax = 7750 + (income - 70000) * 0.20;
      else if (income <= 400000) tax = 33750 + (income - 200000) * 0.225;
      else tax = 78750 + (income - 400000) * 0.25;
    }
    else if (income <= 800000) {
      if (income <= 70000) tax = income * 0.15;
      else if (income <= 200000) tax = 10500 + (income - 70000) * 0.20;
      else if (income <= 400000) tax = 36500 + (income - 200000) * 0.225;
      else tax = 81500 + (income - 400000) * 0.25;
    }
    else if (income <= 900000) {
      if (income <= 200000) tax = income * 0.20;
      else if (income <= 400000) tax = 40000 + (income - 200000) * 0.225;
      else tax = 85000 + (income - 400000) * 0.25;
    }
    else if (income <= 1200000) {
      if (income <= 400000) tax = income * 0.225;
      else tax = 90000 + (income - 400000) * 0.25;
    }
    else {
      // Over 1,200,000
      if (income <= 1200000) tax = income * 0.25;
      else tax = 300000 + (income - 1200000) * 0.275;
    }
    
    return tax;
  };

  const calculateTaxForLowIncome = (income) => {
    let taxRate = 0;
    
    if (income <= 500000) taxRate = 0.004; // 0.4%
    else if (income <= 2000000) taxRate = 0.005; // 0.5%
    else if (income <= 3000000) taxRate = 0.0075; // 0.75%
    else if (income <= 10000000) taxRate = 0.01; // 1%
    else if (income <= 20000000) taxRate = 0.015; // 1.5%
    
    return income * taxRate;
  };

  const handleChat = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    let userMessage = input.trim();
    
    // For number steps, clean the input (remove commas)
    if (conversationStep === 'initial') {
      userMessage = userMessage.replace(/,/g, '');
    }
    
    // For display, keep the formatted version
    const displayMessage = conversationStep === 'initial'
      ? formatNumberWithCommas(userMessage) 
      : userMessage;
    
    setMessages(prev => [...prev, { role: 'user', text: displayMessage }]);

    let botReply = '';
    const userInputLower = userMessage.toLowerCase();
    const isYes = userInputLower === 'yes' || userInputLower === 'نعم' || userInputLower === 'y' || userInputLower === 'اه' || userInputLower === 'ن';
    const isNo = userInputLower === 'no' || userInputLower === 'لا' || userInputLower === 'l' || userInputLower === 'لأ';
    const isNumber = !isNaN(userMessage) && userMessage.length > 0 && userMessage !== '.';
    const isCompany = userInputLower === 'company' || userInputLower === 'شركة' || userInputLower === 'com' || userInputLower.includes('شركة');
    const isIndividual = userInputLower === 'individual' || userInputLower === 'فرد' || userInputLower === 'ind' || userInputLower.includes('فرد');

    switch (conversationStep) {
      case 'initial':
        if (isNumber) {
          const income = parseFloat(userMessage);
          setUserIncome(income);

          const tax = income > 20000000
            ? calculateTaxForHighIncome(income, userType)
            : calculateTaxForLowIncome(income);
          const formattedIncome = formatNumberWithCommas(income);
          const formattedTax = formatNumberWithCommas(tax);

          botReply = language === 'ar'
            ? `بناءً على الدخل السنوي (${formattedIncome} ج.م)، فإن الضريبة السنوية المطلوبة هي: ${formattedTax} ج.م.\n\nهل تريد حساب ضريبة أخرى؟ (نعم/لا)`
            : `Based on annual income (${formattedIncome} EGP), the estimated tax is: ${formattedTax} EGP.\n\nDo you want to calculate another tax? (yes/no)`;
          setConversationStep('ask_another_calculation');
        } else {
          botReply = language === 'ar'
            ? 'من فضلك أدخل قيمة الدخل السنوي كأرقام فقط.'
            : 'Please enter the annual income value as numbers only.';
        }
        break;

      case 'ask_user_type':
        if (isCompany) {
          setUserType('company');
          botReply = language === 'ar'
            ? 'أدخل دخلك السنوي بالجنيه المصري:'
            : 'Enter your annual income in Egyptian Pounds:';
          setConversationStep('initial');
        } else if (isIndividual) {
          setUserType('individual');
          botReply = language === 'ar'
            ? 'أدخل دخلك السنوي بالجنيه المصري:'
            : 'Enter your annual income in Egyptian Pounds:';
          setConversationStep('initial');
        } else {
          botReply = language === 'ar'
            ? 'من فضلك اختر شركة أو فرد.'
            : 'Please choose company or individual.';
        }
        break;

      case 'ask_another_calculation':
        if (isYes) {
          resetConversation();
          botReply = language === 'ar'
            ? 'حسناً، لنبدأ من جديد. أدخل الدخل السنوي بالجنيه المصري:'
            : 'Okay, let\'s start again. Enter annual income in Egyptian Pounds:';
        } else if (isNo) {
          botReply = language === 'ar'
            ? 'شكراً لاستخدامك حاسبة الضرائب الذكية. يمكنك التواصل مع المستشار محمد أبو سريع للاستشارات الأخرى: mohamedabosarie@gmail.com'
            : 'Thank you for using the smart tax calculator. You can contact Consultant Mohamed Abu Sari for other consultations: mohamedabosarie@gmail.com';
          setTimeout(() => {
            closeChat();
            resetConversation();
          }, 3000);
        } else {
          botReply = language === 'ar'
            ? 'من فضلك أجب بنعم أو لا.'
            : 'Please answer yes or no.';
        }
        break;

      default:
        botReply = language === 'ar'
          ? 'عذراً، حدث خطأ. دعنا نبدأ من جديد.'
          : 'Sorry, an error occurred. Let\'s start over.';
        resetConversation();
    }

    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'bot', text: botReply }]);
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    }, 500);
    
    setInput('');
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!messagesContainerRef.current) return;

    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const chatbotText = {
    title: language === 'ar' ? "مساعد الضرائب الذكي M.A.S" : "M.A.S AI Tax Assistant",
    placeholder: language === 'ar' ? "أدخل المبلغ هنا..." : "Enter amount here...",
    note: language === 'ar' ? "حاسبة الضرائب المصرية المتقدمة" : "Advanced Egyptian Tax Calculator",
    close: language === 'ar' ? "إغلاق المحادثة" : "Close chat",
    send: language === 'ar' ? "إرسال" : "Send",
    restart: language === 'ar' ? "بدء محادثة جديدة" : "Start new conversation"
  };

  // Get appropriate input placeholder based on conversation step
  const getInputPlaceholder = () => {
    switch (conversationStep) {
      case 'initial':
        return language === 'ar' ? 'أدخل الدخل السنوي... (أرقام إنجليزية فقط)' : 'Enter annual income... (English numbers only)';
      case 'ask_user_type':
        return language === 'ar' ? 'شركة أو فرد' : 'company or individual';
      case 'ask_another_calculation':
        return language === 'ar' ? 'نعم أو لا' : 'yes or no';
      default:
        return chatbotText.placeholder;
    }
  };

  return (
    <>
      {/* Floating button trigger */}
      {!isOpen && (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 flex flex-row md:flex-col items-end gap-2">
          {showPromptMessage && (
            <div
              dir="rtl"
              className="w-[190px] md:w-[240px] rounded-xl bg-white/80 backdrop-blur-sm text-gray-900 px-3 py-2 text-xs md:text-sm font-semibold shadow-lg border border-blue-200"
            >
              <div className="relative" dir="rtl">
                <p className="leading-snug pl-6">
                  مش عايز تحسب ضرائب بتاعتك ؟
                </p>
                <button
                  type="button"
                  onClick={() => setShowPromptMessage(false)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 leading-none"
                  aria-label={language === 'ar' ? 'إغلاق الرسالة' : 'Close message'}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <button
            onClick={openChat}
            className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 border-2 border-blue-300 shadow-2xl transition-all hover:scale-110 hover:shadow-blue-300/50 active:scale-95 overflow-hidden"
            aria-label={chatbotText.title}
          >
            <img
              src="/bot-head.svg"
              alt="Bot"
              className="w-full h-full object-cover"
              draggable="false"
            />
            {unreadCount > 0 ? (
              <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></span>
            )}
          </button>
        </div>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-end md:items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900/95 to-black/95 backdrop-blur-xl border border-blue-500/30 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calculator size={22} className="text-white" />
                <span className="font-bold text-white">{chatbotText.title}</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={resetConversation}
                  className="p-2 hover:bg-black/30 rounded-full transition-colors"
                  aria-label={chatbotText.restart}
                  title={chatbotText.restart}
                >
                  <Repeat size={18} className="text-white" />
                </button>
                <span className="text-xs bg-black/30 px-3 py-1.5 rounded-full text-white/90">
                  M.A.S AI
                </span>
                <button 
                  onClick={() => {
                    closeChat();
                    resetConversation();
                  }}
                  className="p-2 hover:bg-black/30 rounded-full transition-colors"
                  aria-label={chatbotText.close}
                >
                  <X size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Messages Container */}
            <div ref={messagesContainerRef} className="h-80 overflow-y-auto p-4 flex flex-col gap-3 chat-scrollbar">
              {messages.map((m, i) => (
                <div 
                  key={i} 
                  className={`p-4 rounded-2xl max-w-[85%] animate-slideIn ${m.role === 'user' 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 self-end text-white' 
                    : 'bg-gradient-to-r from-gray-800 to-gray-900 self-start text-white/90 border border-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {m.role === 'bot' && (
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-xs font-bold">AI</span>
                      </div>
                    )}
                    <p className={`whitespace-pre-line ${m.role === 'bot' && language === 'ar' ? 'text-right' : 'text-left'} ${language === 'ar' ? 'font-arabic' : 'font-english'}`}>
                      {m.text}
                    </p>
                    {m.role === 'user' && (
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-xs font-bold">U</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Input Form */}
            <form onSubmit={handleChat} className="p-4 border-t border-white/10 bg-black/30">
              <div className="flex gap-3">
                <input
                  value={input}
                  onChange={handleInputChange}
                  className="flex-1 bg-white/10 p-3 rounded-xl outline-none text-white placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={getInputPlaceholder()}
                  dir={language === 'ar' ? 'rtl' : 'ltr'}
                  aria-label="Chat input"
                  // Force English keyboard for number steps
                  inputMode={conversationStep === 'initial' ? "numeric" : "text"}
                />
                <button 
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 p-3 rounded-xl hover:from-blue-500 hover:to-blue-400 transition-all hover:shadow-lg active:scale-95"
                  aria-label={chatbotText.send}
                >
                  <Send size={20} className="text-white" />
                </button>
              </div>
              <div className="flex justify-between items-center mt-3">
                <p className="text-xs text-gray-400">
                  {conversationStep === 'initial'
                    ? (language === 'ar' ? 'أرقام إنجليزية مع فواصل آلاف' : 'English numbers with comma formatting')
                    : (language === 'ar' ? 'أدخل نصاً' : 'Enter text')
                  }
                </p>
                <div className="flex gap-2">
                  {conversationStep === 'ask_another_calculation' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setInput(language === 'ar' ? 'نعم' : 'yes')}
                        className="text-xs px-2 py-1 bg-green-900/30 text-green-300 rounded hover:bg-green-900/50"
                      >
                        {language === 'ar' ? 'نعم' : 'yes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setInput(language === 'ar' ? 'لا' : 'no')}
                        className="text-xs px-2 py-1 bg-red-900/30 text-red-300 rounded hover:bg-red-900/50"
                      >
                        {language === 'ar' ? 'لا' : 'no'}
                      </button>
                    </>
                  ) : conversationStep === 'ask_user_type' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setInput(language === 'ar' ? 'شركة' : 'company')}
                        className="text-xs px-2 py-1 bg-blue-900/30 text-blue-300 rounded hover:bg-blue-900/50"
                      >
                        {language === 'ar' ? 'شركة' : 'company'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setInput(language === 'ar' ? 'فرد' : 'individual')}
                        className="text-xs px-2 py-1 bg-purple-900/30 text-purple-300 rounded hover:bg-purple-900/50"
                      >
                        {language === 'ar' ? 'فرد' : 'individual'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setInput(formatNumberWithCommas(1000000))}
                        className="text-xs px-2 py-1 bg-green-900/30 text-green-300 rounded hover:bg-green-900/50"
                      >
                        {formatNumberWithCommas(1000000)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setInput(formatNumberWithCommas(50000))}
                        className="text-xs px-2 py-1 bg-blue-900/30 text-blue-300 rounded hover:bg-blue-900/50"
                      >
                        {formatNumberWithCommas(50000)}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}